// src/components/shop/MassLocationUpdater.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { MapPin, X, CheckCircle, AlertCircle } from 'lucide-react';
import AddressInput from '../AddressInput';

const UpdaterOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const UpdaterModal = styled.div`
  background: ${props => props.theme?.colors?.surface || '#1a1a1a'};
  border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${props => `${props.theme?.colors?.accent}30`};

  h2 {
    color: ${props => props.theme?.colors?.accent};
    font-size: 1.3rem;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme?.colors?.text};
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => `${props.theme?.colors?.accent}20`};
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const InfoText = styled.p`
  color: ${props => `${props.theme?.colors?.text}90`};
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1.5rem;
`;

const ItemCount = styled.div`
  background: ${props => `${props.theme?.colors?.accent}20`};
  border: 1px solid ${props => `${props.theme?.colors?.accent}40`};
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  text-align: center;

  .count {
    font-size: 2rem;
    font-weight: bold;
    color: ${props => props.theme?.colors?.accent};
    margin-bottom: 0.25rem;
  }

  .label {
    color: ${props => props.theme?.colors?.text};
    font-size: 0.9rem;
    opacity: 0.8;
  }
`;

const ActionButton = styled.button`
  width: 100%;
  background: ${props => props.theme?.colors?.accent || '#800000'};
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  margin-top: 1rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props => `${props.theme?.colors?.accent}60`};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div`
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;

  &.success {
    background: rgba(76, 175, 80, 0.2);
    color: #4CAF50;
    border: 1px solid rgba(76, 175, 80, 0.4);
  }

  &.error {
    background: rgba(244, 67, 54, 0.2);
    color: #f44336;
    border: 1px solid rgba(244, 67, 54, 0.4);
  }
`;

const MassLocationUpdater = ({ 
  onClose, 
  onLocationSet, 
  theme, 
  itemCount,
  isService,
  onMapOpen 
}) => {
  const [location, setLocation] = useState({ address: '', coordinates: null });
  const [status, setStatus] = useState(null);

  const handleLocationSelect = (selectedLocation) => {
    if (selectedLocation?.coordinates) {
      setLocation({
        address: selectedLocation.address,
        coordinates: selectedLocation.coordinates
      });
      setStatus(null);
    }
  };

  const handleApply = () => {
    if (!location.coordinates) {
      setStatus({ 
        type: 'error', 
        message: 'Please select a location first' 
      });
      return;
    }

    onLocationSet(location);
    setStatus({ 
      type: 'success', 
      message: `Location updated for ${itemCount} ${isService ? 'services' : 'items'}!` 
    });

    // Close after brief delay
    setTimeout(() => {
      onClose();
    }, 1500);
  };

  return (
    <UpdaterOverlay onClick={onClose}>
      <UpdaterModal theme={theme} onClick={(e) => e.stopPropagation()}>
        <ModalHeader theme={theme}>
          <h2>
            <MapPin size={22} />
            Set Location for All
          </h2>
          <CloseButton theme={theme} onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <InfoText theme={theme}>
          Set a single location for all {isService ? 'services' : 'products'} at once. 
          This is useful when all items share the same pickup/delivery location.
        </InfoText>

        <ItemCount theme={theme}>
          <div className="count">{itemCount}</div>
          <div className="label">
            {isService ? 'Services' : 'Items'} will be updated
          </div>
        </ItemCount>

        <AddressInput
          address={location.address}
          onAddressChange={(value) => setLocation({ ...location, address: value })}
          onLocationSelect={handleLocationSelect}
          onMapOpen={onMapOpen}
        />

        <ActionButton
          theme={theme}
          onClick={handleApply}
          disabled={!location.coordinates}
        >
          <CheckCircle size={18} />
          Apply to All {isService ? 'Services' : 'Items'}
        </ActionButton>

        {status && (
          <StatusMessage className={status.type} theme={theme}>
            {status.type === 'success' ? (
              <CheckCircle size={18} />
            ) : (
              <AlertCircle size={18} />
            )}
            {status.message}
          </StatusMessage>
        )}
      </UpdaterModal>
    </UpdaterOverlay>
  );
};

export default MassLocationUpdater;